/**
 * One place that builds every page's head tags.
 *
 * Every public route calls `seo()`. It returns the `meta` + `links` arrays that
 * TanStack Router's `head()` expects, so canonical / og:url / og:image can
 * never drift per page again.
 */

export const SITE_URL = "https://jobcues.com";
export const SITE_NAME = "JobCues";
export const OG_IMAGE = `${SITE_URL}/og.png`;
export const TWITTER_HANDLE = "@usejobcues";

/** Build a dynamic OG image URL via /api/og.png. Falls back to static OG if no args. */
export function ogImageUrl(params: {
  title: string;
  description?: string;
  label?: string;
}): string {
  const url = new URL(`${SITE_URL}/api/og.png`);
  url.searchParams.set("title", params.title);
  if (params.description) url.searchParams.set("description", params.description);
  if (params.label) url.searchParams.set("label", params.label);
  return url.toString();
}

type SeoInput = {
  title: string;
  description: string;
  /** Path with a leading slash, e.g. "/jobs". Omit for the current page only when it is not indexable. */
  path?: string;
  image?: string;
  type?: "website" | "article";
  /** Private / duplicate surfaces. Adds robots noindex and drops the canonical. */
  noindex?: boolean;
  /** ISO date strings for article-type pages. Freshness is a ranking + AI-citation signal. */
  publishedTime?: string;
  modifiedTime?: string;
};

export function seo({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
  publishedTime,
  modifiedTime,
}: SeoInput) {
  const url = path ? canonicalUrl(path) : undefined;
  const ogImage =
    image ??
    (path === "/"
      ? OG_IMAGE
      : title && title !== SITE_NAME
        ? ogImageUrl({
            title,
            description: description ? description.slice(0, 140) : undefined,
          })
        : OG_IMAGE);

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    { property: "og:image", content: ogImage },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: title },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: TWITTER_HANDLE },
    { name: "twitter:creator", content: TWITTER_HANDLE },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImage },
  ];

  if (url) meta.push({ property: "og:url", content: url });
  if (publishedTime) meta.push({ property: "article:published_time", content: publishedTime });
  if (modifiedTime) meta.push({ property: "article:modified_time", content: modifiedTime });

  meta.push({
    name: "robots",
    // "follow", not "nofollow": these pages still link to indexable ones, and
    // nofollow only stops the crawler discovering them.
    content: noindex
      ? "noindex, follow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  });

  const links: Array<Record<string, string>> = [];
  if (url && !noindex) links.push({ rel: "canonical", href: url });

  return { meta, links };
}

export function canonicalUrl(path: string) {
  if (path === "/") return `${SITE_URL}/`;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`.replace(/\/+$/, "");
}

/**
 * JSON-LD as a TanStack `head().scripts` entry. Returning a string child keeps
 * it in the server-rendered HTML, which is the only version AI crawlers read —
 * they do not execute JavaScript.
 */
export function jsonLdScript(data: unknown) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(data),
  };
}
